import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { DiaryDetailBody, DiaryListItem, ParentDiaryService } from './parent-diary.service';

type DiaryTab = 'today' | 'upcoming' | 'past';

// Diary — list (Today/Upcoming/Past tabs) + detail view. Prev/Next page
// buttons (not infinite scroll) using the API's own maxPageNumber, and
// Upcoming/Past default to "All dates" (no calendar picker yet) — see the
// API reference this was scoped from. Rendered conditionally inside
// ParentLoginComponent's dashboard (no routing).
@Component({
  selector: 'app-parent-diary',
  templateUrl: './parent-diary.component.html',
  styleUrls: ['./parent-diary.component.css'],
})
export class ParentDiaryComponent implements OnInit, OnChanges {

  @Input() studentId = '';
  @Input() classId = '';
  @Input() studentName = '';

  activeTab: DiaryTab = 'today';
  items: DiaryListItem[] = [];
  listLoading = false;
  listError = '';
  private listRequestId = 0;
  currentPage = 1;
  maxPageNumber = 1;

  subjects: { subjectId: string; subject: string }[] = [];
  selectedSubjectId: string | null = null;

  selectedItem: DiaryListItem | null = null;
  detail: DiaryDetailBody['diaryData'] | null = null;
  detailLoading = false;
  detailError = '';

  markingDoneId: string | null = null;
  confirmMarkDoneItem: DiaryListItem | null = null;

  // In-app media preview state (image viewer + PDF viewer) — per
  // DiaryDetailedView.js's "full-screen swipeable image viewer" and
  // "in-app PDF viewer with a download button", simplified for web:
  // prev/next buttons instead of swipe, <iframe> instead of a native PDF
  // renderer.
  imageViewerIndex: number | null = null;
  pdfViewerUrl: SafeResourceUrl | null = null;
  pdfViewerDownloadUrl: string | null = null;

  constructor(private diaryService: ParentDiaryService, private sanitizer: DomSanitizer) { }

  ngOnInit(): void {
    if (this.studentId && this.classId) {
      this.loadSubjects();
      this.loadList();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['studentId'] || changes['classId']) && this.studentId && this.classId) {
      // A student switch must not carry over the previous student's
      // in-progress UI state (open detail view, subject filter, page).
      this.selectedItem = null;
      this.detail = null;
      this.selectedSubjectId = null;
      this.currentPage = 1;
      this.activeTab = 'today';
      this.loadSubjects();
      this.loadList();
    }
  }

  private get lang(): string {
    // Deliberately always 'en', not the UI's selected display language —
    // the parent mobile app's backend endpoints expect this constant
    // regardless of what language the user has the interface in; switching
    // the UI language must never change this API payload field.
    return 'en';
  }

  setTab(tab: DiaryTab): void {
    if (this.activeTab === tab) { return; }
    this.activeTab = tab;
    this.currentPage = 1;
    this.loadList();
  }

  setSubjectFilter(subjectId: string | null): void {
    this.selectedSubjectId = subjectId;
    this.currentPage = 1;
    this.loadList();
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.maxPageNumber || page === this.currentPage) { return; }
    this.currentPage = page;
    this.loadList();
  }

  goToPrevPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  goToNextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  private loadSubjects(): void {
    this.diaryService.getSubjectFilterList(this.studentId, this.classId, this.lang).subscribe({
      next: (response: any) => {
        if (response && response.status && response.body) {
          this.subjects = response.body.subjectListForFilter || [];
        }
      },
      error: () => { /* subject filter is a nice-to-have; fail silently */ },
    });
  }

  loadList(): void {
    if (!this.studentId || !this.classId) { return; }
    this.listLoading = true;
    this.listError = '';

    const call$ = this.activeTab === 'today'
      ? this.diaryService.getTodaysDiary(this.studentId, this.classId, this.currentPage, this.selectedSubjectId, this.lang)
      : this.activeTab === 'upcoming'
        ? this.diaryService.getUpcomingDiary(this.studentId, this.classId, this.currentPage, this.selectedSubjectId, this.lang)
        : this.diaryService.getPastDiary(this.studentId, this.classId, this.currentPage, this.selectedSubjectId, this.lang);

    // Bumped on every call so a still-in-flight request for the PREVIOUS
    // student (e.g. this screen's initial load hadn't finished when the
    // parent switched student) can't land after the new student's request
    // and overwrite its fresher data with stale data.
    const requestId = ++this.listRequestId;
    call$.subscribe({
      next: (response: any) => {
        if (requestId !== this.listRequestId) { return; }
        this.listLoading = false;
        if (response && response.status && response.body) {
          const body = response.body;
          this.items = body.diaryDetailsForTodaysDueDates
            || body.diaryDetailsForUpcomingDueDates
            || body.diaryDetailsForPastDueDates
            || [];
          this.maxPageNumber = body.maxPageNumber || 1;
        } else {
          this.items = [];
          this.maxPageNumber = 1;
          this.listError = (response && response.body && response.body.message) || 'Unable to load diary.';
        }
      },
      error: () => {
        if (requestId !== this.listRequestId) { return; }
        this.listLoading = false;
        this.items = [];
        this.maxPageNumber = 1;
        this.listError = 'Unable to load diary.';
      },
    });
  }

  trackByDiaryId(index: number, item: DiaryListItem): string {
    return item.diaryId;
  }

  requestMarkDone(item: DiaryListItem, event: Event): void {
    event.stopPropagation();
    if (item.isHomeworkDone) { return; }
    this.confirmMarkDoneItem = item;
  }

  cancelMarkDone(): void {
    this.confirmMarkDoneItem = null;
  }

  confirmMarkDone(): void {
    const item = this.confirmMarkDoneItem;
    if (!item) { return; }
    this.confirmMarkDoneItem = null;
    this.markingDoneId = item.diaryId;
    this.diaryService.markHomeworkDone(this.studentId, this.classId, item.diaryId, this.lang).subscribe({
      next: (response: any) => {
        this.markingDoneId = null;
        if (response && response.status) {
          item.isHomeworkDone = 1;
        }
      },
      error: () => {
        this.markingDoneId = null;
      },
    });
  }

  openDetail(item: DiaryListItem): void {
    this.selectedItem = item;
    this.detail = null;
    this.detailError = '';
    this.detailLoading = true;
    // The detail endpoint requires a real subjectId — it 422s ("Please
    // provide valid subject id") when it's omitted, so the list's active
    // filter (null when "All Subjects" is selected) isn't enough on its
    // own. Each diary item belongs to exactly one subject, so use that
    // item's own subjectId as the source of truth and only fall back to
    // the list filter if the item itself doesn't carry one.
    const subjectId = item.subjectId || this.selectedSubjectId;
    this.diaryService.getDiaryDetail(this.studentId, this.classId, item.diaryId, subjectId, this.lang).subscribe({
      next: (response: any) => {
        this.detailLoading = false;
        if (response && response.status && response.body && response.body.diaryData) {
          this.detail = response.body.diaryData;
          // TEMP: log the raw diaryMediaDetails shape so a mismatch against
          // DiaryMediaType/DiaryMediaUrl (see detailImages/detailDocuments)
          // can be spotted directly, if media still doesn't show up after
          // this fix — remove once the real field names are confirmed.
          console.log('[diary detail] diaryMediaDetails:', this.detail?.diaryMediaDetails);
        } else {
          this.detailError = 'Unable to load this diary entry.';
        }
      },
      error: () => {
        this.detailLoading = false;
        this.detailError = 'Unable to load this diary entry.';
      },
    });
  }

  closeDetail(): void {
    this.selectedItem = null;
    this.detail = null;
    this.detailError = '';
  }

  // Case-insensitive, tolerant of a few likely real-world spellings — the
  // API reference's minimal example ("image"/"document") already proved
  // out of sync with the live backend once (see the subjectId 422 fix
  // above), so match loosely here rather than assume exact casing/wording.
  get detailImages() {
    return (this.detail?.diaryMediaDetails || []).filter(m => {
      const type = (m.DiaryMediaType || '').toString().toLowerCase();
      return type.includes('image') || type.includes('img') || type.includes('photo');
    });
  }

  get detailDocuments() {
    return (this.detail?.diaryMediaDetails || []).filter(m => {
      const type = (m.DiaryMediaType || '').toString().toLowerCase();
      return type.includes('doc') || type.includes('pdf') || type.includes('file');
    });
  }

  openImageViewer(index: number): void {
    this.imageViewerIndex = index;
  }

  closeImageViewer(): void {
    this.imageViewerIndex = null;
  }

  showPrevImage(): void {
    if (this.imageViewerIndex === null) { return; }
    this.imageViewerIndex = (this.imageViewerIndex - 1 + this.detailImages.length) % this.detailImages.length;
  }

  showNextImage(): void {
    if (this.imageViewerIndex === null) { return; }
    this.imageViewerIndex = (this.imageViewerIndex + 1) % this.detailImages.length;
  }

  get currentViewerImageUrl(): string | null {
    if (this.imageViewerIndex === null) { return null; }
    return this.detailImages[this.imageViewerIndex]?.DiaryMediaUrl || null;
  }

  openPdfViewer(url: string): void {
    this.pdfViewerDownloadUrl = url;
    this.pdfViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  closePdfViewer(): void {
    this.pdfViewerUrl = null;
    this.pdfViewerDownloadUrl = null;
  }

  // "description (with clickable links highlighted)" per DiaryDetailedView.js
  // — escape the plain-text description first (it's staff-authored free
  // text, not trusted markup) so nothing but the URLs we add ourselves
  // becomes a tag, then wrap bare URLs as links.
  linkify(text: string): SafeHtml {
    if (!text) { return ''; }
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    const linked = escaped.replace(
      /(https?:\/\/[^\s<]+)/g,
      '<a href="$1" target="_blank" rel="noopener" class="pdi-detail-link">$1</a>'
    );
    return this.sanitizer.bypassSecurityTrustHtml(linked);
  }
}
